/**
 * AreaSelectionMethod - Selection UI for how the user defines the flight area
 * Options: Draw polygon, Select UTM grid squares, Import from file (KML/Shapefile)
 */

import { useState, useRef } from 'react';
import styled from 'styled-components';
import { LuPencil, LuMap, LuFileDown, LuArrowLeft, LuX, LuCheck } from 'react-icons/lu';
import { FormGroup } from './FlightPlannerStyles';
import GridReferenceSearch from './GridReferenceSearch';

const MethodGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const MethodCard = styled.div`
	padding: 1rem;
	border: 1px solid
		${props => (props.$selected ? props.theme.colors.green : 'rgba(52, 211, 153, 0.08)')};
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.25s ease;
	background: ${props =>
		props.$selected ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.02)'};

	&:hover {
		border-color: ${props => props.theme.colors.green};
		background: rgba(52, 211, 153, 0.06);
		transform: translateY(-1px);
		box-shadow: 0 4px 16px rgba(52, 211, 153, 0.08);
	}
`;

const MethodTitle = styled.h4`
	color: ${props => props.theme.colors.textColor};
	font-size: 0.9rem;
	margin: 0 0 0.25rem 0;
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const MethodDescription = styled.p`
	color: ${props => props.theme.colors.textAccent};
	font-size: 0.85rem;
	margin: 0;
	line-height: 1.4;
`;

const FileDropZone = styled.div`
	border: 2px dashed
		${props => (props.$dragOver ? props.theme.colors.green : 'rgba(52, 211, 153, 0.2)')};
	border-radius: 10px;
	padding: 1.5rem;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
	background: ${props =>
		props.$dragOver ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.02)'};
	margin-top: 0.75rem;

	&:hover {
		border-color: ${props => props.theme.colors.green};
		background: rgba(52, 211, 153, 0.04);
	}
`;

const FileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: rgba(52, 211, 153, 0.06);
	border: 1px solid rgba(52, 211, 153, 0.15);
	border-radius: 8px;
	margin-top: 0.75rem;
`;

const FileName = styled.span`
	flex: 1;
	color: ${props => props.theme.colors.textColor};
	font-size: 0.85rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const RemoveButton = styled.button`
	background: none;
	border: none;
	color: #ff6b6b;
	cursor: pointer;
	padding: 2px;
	display: flex;
	align-items: center;
	opacity: 0.7;

	&:hover {
		opacity: 1;
	}
`;

const ErrorMessage = styled.div`
	padding: 0.75rem;
	margin-top: 0.75rem;
	border-radius: 8px;
	background: rgba(248, 113, 113, 0.1);
	border: 1px solid rgba(248, 113, 113, 0.2);
	color: #f87171;
	font-size: 0.85rem;
`;

const GridSizeSelector = styled.div`
	margin-top: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	> div {
		margin-bottom: 0;
	}
`;

const HelpText = styled.small`
	color: #888;
	font-size: 0.8rem;
	display: block;
	line-height: 1.4;
`;

export const AREA_METHODS = {
	DRAW: 'draw',
	UTM_GRID: 'utm-grid',
	IMPORT_FILE: 'import-file',
};

export const UTM_GRID_SIZES = [
	{ id: '100m', label: '100 m', meters: 100 },
	{ id: '250m', label: '250 m', meters: 250 },
	{ id: '500m', label: '500 m', meters: 500 },
	{ id: '1km', label: '1 km', meters: 1000 },
	{ id: '5km', label: '5 km', meters: 5000 },
	{ id: '10km', label: '10 km', meters: 10000 },
];

/**
 * Parse KML file and extract polygon coordinates
 * Returns array of {lat, lng} points
 */
const parseKML = text => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(text, 'text/xml');

	const parseError = doc.querySelector('parsererror');
	if (parseError) throw new Error('Invalid KML file');

	// Look for coordinates in Polygon, LineString, or LinearRing
	const coordElements = doc.querySelectorAll('coordinates');
	if (coordElements.length === 0) throw new Error('No coordinates found in KML file');

	// Find the first polygon or use the first coordinate set
	const polygons = doc.querySelectorAll('Polygon');
	let coordText;

	if (polygons.length > 0) {
		const outerBoundary = polygons[0].querySelector('outerBoundaryIs coordinates');
		coordText = outerBoundary ? outerBoundary.textContent : coordElements[0].textContent;
	} else {
		coordText = coordElements[0].textContent;
	}

	const points = coordText
		.trim()
		.split(/\s+/)
		.map(coord => {
			const [lng, lat] = coord.split(',').map(Number);
			if (isNaN(lat) || isNaN(lng)) return null;
			return { lat, lng };
		})
		.filter(Boolean);

	if (points.length < 3) throw new Error('KML must contain at least 3 coordinate points');

	// Remove closing point if it duplicates the first
	const last = points[points.length - 1];
	const first = points[0];
	if (Math.abs(last.lat - first.lat) < 1e-8 && Math.abs(last.lng - first.lng) < 1e-8) {
		points.pop();
	}

	return points;
};

/**
 * Parse GeoJSON and extract polygon coordinates
 */
const parseGeoJSON = text => {
	const geojson = JSON.parse(text);

	let coords;
	if (geojson.type === 'FeatureCollection') {
		const polyFeature = geojson.features?.find(
			f => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
		);
		if (!polyFeature) throw new Error('No polygon feature found in GeoJSON');
		coords =
			polyFeature.geometry.type === 'MultiPolygon'
				? polyFeature.geometry.coordinates[0][0]
				: polyFeature.geometry.coordinates[0];
	} else if (geojson.type === 'Feature') {
		if (geojson.geometry?.type === 'MultiPolygon') {
			coords = geojson.geometry.coordinates[0][0];
		} else if (geojson.geometry?.type === 'Polygon') {
			coords = geojson.geometry.coordinates[0];
		} else {
			throw new Error('Feature must be a Polygon or MultiPolygon');
		}
	} else if (geojson.type === 'Polygon') {
		coords = geojson.coordinates[0];
	} else if (geojson.type === 'MultiPolygon') {
		coords = geojson.coordinates[0][0];
	} else {
		throw new Error('Unsupported GeoJSON type: ' + geojson.type);
	}

	const points = coords.map(([lng, lat]) => ({ lat, lng }));

	// Remove closing point
	const last = points[points.length - 1];
	const first = points[0];
	if (
		points.length > 3 &&
		Math.abs(last.lat - first.lat) < 1e-8 &&
		Math.abs(last.lng - first.lng) < 1e-8
	) {
		points.pop();
	}

	if (points.length < 3) throw new Error('Polygon must have at least 3 points');
	return points;
};

const AreaSelectionMethod = ({
	selectedMethod,
	onMethodChange,
	onFileImport,
	gridSize,
	onGridSizeChange,
	onGridSearch,
	importedFileName,
	onClearImport,
}) => {
	const [dragOver, setDragOver] = useState(false);
	const [error, setError] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const fileInputRef = useRef(null);

	const handleFileProcess = async file => {
		setError(null);
		setIsProcessing(true);

		try {
			const name = file.name.toLowerCase();
			let points;

			if (name.endsWith('.kml') || name.endsWith('.kmz')) {
				const text = await file.text();
				points = parseKML(text);
			} else if (name.endsWith('.geojson') || name.endsWith('.json')) {
				const text = await file.text();
				points = parseGeoJSON(text);
			} else {
				throw new Error('Unsupported file format. Use KML or GeoJSON.');
			}

			onFileImport(points, file.name);
		} catch (err) {
			setError(err.message);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleDrop = e => {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files[0];
		if (file) handleFileProcess(file);
	};

	const handleFileSelect = e => {
		const file = e.target.files[0];
		if (file) handleFileProcess(file);
		e.target.value = '';
	};

	return (
		<MethodGrid>
			<MethodCard
				$selected={selectedMethod === AREA_METHODS.DRAW}
				onClick={() => onMethodChange(AREA_METHODS.DRAW)}>
				<MethodTitle>
					<LuPencil size={16} /> Draw Polygon
				</MethodTitle>
				<MethodDescription>
					Click on the map to draw a custom flight area polygon.
				</MethodDescription>
			</MethodCard>

			<MethodCard
				$selected={selectedMethod === AREA_METHODS.UTM_GRID}
				onClick={() => onMethodChange(AREA_METHODS.UTM_GRID)}>
				<MethodTitle>
					<LuMap size={16} /> Select from UTM Grid
				</MethodTitle>
				<MethodDescription>
					Select one or more UTM/MGRS grid squares from the map to define the flight area.
				</MethodDescription>
			</MethodCard>

			{selectedMethod === AREA_METHODS.UTM_GRID && (
				<GridSizeSelector>
					<FormGroup>
						<label>Grid Square Size:</label>
						<select
							value={gridSize}
							onChange={e => onGridSizeChange(e.target.value)}>
							{UTM_GRID_SIZES.map(size => (
								<option key={size.id} value={size.id}>
									{size.label}
								</option>
							))}
						</select>
					</FormGroup>
					<HelpText>
						Click grid squares on the map to select them. Click again to deselect.
						Selected squares will be merged into a flight area.
					</HelpText>
					<FormGroup>
						<label>Search Grid Reference:</label>
						<GridReferenceSearch onNavigate={onGridSearch} />
					</FormGroup>
					<HelpText>
						Accepts UTM (32V 661965 7266000), MGRS (32VNM6196528316), or grid
						label (32/500E/6600N).
					</HelpText>
				</GridSizeSelector>
			)}

			<MethodCard
				$selected={selectedMethod === AREA_METHODS.IMPORT_FILE}
				onClick={() => onMethodChange(AREA_METHODS.IMPORT_FILE)}>
				<MethodTitle>
					<LuFileDown size={16} /> Import from File
				</MethodTitle>
				<MethodDescription>
					Import a KML or GeoJSON file to define the flight area.
				</MethodDescription>
			</MethodCard>

			{selectedMethod === AREA_METHODS.IMPORT_FILE && (
				<>
					{!importedFileName ? (
						<FileDropZone
							$dragOver={dragOver}
							onDragOver={e => {
								e.preventDefault();
								setDragOver(true);
							}}
							onDragLeave={() => setDragOver(false)}
							onDrop={handleDrop}
							onClick={() => fileInputRef.current?.click()}>
							<LuArrowLeft
								size={24}
								style={{
									color: '#34d399',
									marginBottom: '0.5rem',
									transform: 'rotate(90deg)',
								}}
							/>
							<div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
								{isProcessing
									? 'Processing...'
									: 'Drop file here or click to browse'}
							</div>
							<div
								style={{
									color: '#64748b',
									fontSize: '0.75rem',
									marginTop: '0.25rem',
								}}>
								KML, GeoJSON
							</div>
							<input
								ref={fileInputRef}
								type="file"
								accept=".kml,.kmz,.geojson,.json"
								onChange={handleFileSelect}
								style={{ display: 'none' }}
							/>
						</FileDropZone>
					) : (
						<FileInfo>
							<LuCheck size={16} style={{ color: '#34d399', flexShrink: 0 }} />
							<FileName>{importedFileName}</FileName>
							<RemoveButton onClick={onClearImport} title="Remove file">
								<LuX size={14} />
							</RemoveButton>
						</FileInfo>
					)}

					{error && <ErrorMessage>{error}</ErrorMessage>}
				</>
			)}
		</MethodGrid>
	);
};

export default AreaSelectionMethod;
